import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Database, Sparkles, Lightbulb, ListTodo, X } from 'lucide-react';
import { clsx } from 'clsx';

const DISMISSED_KEY = 'levelup-legacy:getting-started-dismissed';

interface StepDef {
  icon: typeof Database;
  title: string;
  description: string;
  link: string;
  linkLabel: string;
}

const STEPS: StepDef[] = [
  {
    icon: Database,
    title: 'Connect your data',
    description: 'Link your financial accounts and verify your data sources are active.',
    link: '/settings',
    linkLabel: 'Check Data Sources',
  },
  {
    icon: Sparkles,
    title: 'Run your first analysis',
    description: 'AI analyzes your financial data and generates personalized recommendations.',
    link: '/agents',
    linkLabel: 'Run Analysis',
  },
  {
    icon: Lightbulb,
    title: 'Review recommendations',
    description: 'Accept the ones that fit your strategy, or set them aside for later.',
    link: '/recommendations',
    linkLabel: 'View Recommendations',
  },
  {
    icon: ListTodo,
    title: 'Create an action plan',
    description: 'Turn accepted recommendations into step-by-step action items you can track.',
    link: '/plans',
    linkLabel: 'View Action Plans',
  },
];

export function GettingStartedCard() {
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISSED_KEY) === 'true',
  );

  if (dismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setDismissed(true);
  };

  return (
    <div className="card border border-legacy-200 bg-legacy-50/30">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Getting Started</h2>
          <p className="text-sm text-gray-600 mt-0.5">
            Follow these steps to get the most out of Legacy.
          </p>
        </div>
        <button
          onClick={handleDismiss}
          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          title="Dismiss"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {STEPS.map((step, i) => (
          <Link
            key={step.title}
            to={step.link}
            className={clsx(
              'flex flex-col p-3 rounded-lg border border-gray-200 bg-white hover:border-legacy-300 hover:shadow-sm transition-all group',
            )}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-legacy-100 text-legacy-700 text-xs font-bold shrink-0">
                {i + 1}
              </div>
              <step.icon className="h-4 w-4 text-legacy-600" />
            </div>
            <h3 className="text-sm font-medium text-gray-900">{step.title}</h3>
            <p className="text-xs text-gray-500 mt-1 flex-1">{step.description}</p>
            <span className="text-xs text-legacy-600 font-medium mt-2 group-hover:underline">
              {step.linkLabel} &rarr;
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
